import { toast } from '@/hooks/errors/use-toast';
import { Task } from '@/types/tasks';
import { User } from '@supabase/supabase-js';


/**
 * Hook to provide task mutation functions
 */
export const useTaskMutations = (
  user: User | null,
  _workspaceError: boolean, // Parameter kept for compatibility but not used
  localActiveTasks: Task[],
  localCompletedTasks: Task[],
  setLocalActiveTasks: (tasks: Task[]) => void,
  setLocalCompletedTasks: (tasks: Task[]) => void,
  dbAddTask: (task: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => void,
  dbUpdateTask: ({ taskId, updates, silent }: { taskId: string, updates: Partial<Task>, silent?: boolean }) => void,
  dbRemoveTask: (taskId: string) => void
) => {
  // ❌ DISABLED: Context sync for agent threads (causing 500 errors)


  // Add a new task
  const addTask = (task: Omit<Task, 'id' | 'isCompleted' | 'createdAt'>) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please sign in to add tasks",
        variant: "error",
      });
      return;
    }
    
    // Workspace error check removed
    
    // Create optimistic task for immediate UI update
    const optimisticTask: Task = {
      id: `temp-${Date.now()}`, // Temporary ID
      title: task.title,
      description: task.description || '',
      isCompleted: false,
      dueDate: task.dueDate,
      priority: task.priority,
      urgency: task.urgency,
      createdAt: new Date().toISOString(),
      cardOrder: task.cardOrder || -1000000, // Place at bottom
      goalId: task.goalId
    };
    
    // Add to local state immediately for instant UI feedback
    const updatedActiveTasks = [...localActiveTasks, optimisticTask];
    setLocalActiveTasks(updatedActiveTasks);
    
    // ❌ DISABLED: Sync context for new active task (causing 500 errors)
    // if (task.goalId) {
    //   try {
    //     const allTasks = [...updatedActiveTasks, ...localCompletedTasks];
          // Context sync removed - not critical for core functionality
    //   } catch (error) {
    //     // Silent failure - don't break task creation
    //     console.warn('[TaskMutations] Context sync failed for new task:', error);
    //   }
    // }
    
    // For new tasks, we'll place them at the bottom (low card_order)
    // The database function will calculate a suitable value
    dbAddTask(task);
  };
  
  // Update task order - modified to ensure proper persistence
  const updateTaskOrder = (taskId: string, newOrder: number) => {
    
    // Find the task in our local state
    const task = [...localActiveTasks, ...localCompletedTasks].find(t => t.id === taskId);
    if (!task) return;
    
    // Only update if the order has actually changed
    if (task.cardOrder === newOrder) return;
    
    // Update the database with silent=true to skip toast
    dbUpdateTask({ 
      taskId, 
      updates: { cardOrder: newOrder }, 
      silent: true 
    });
    
    // Update the local state to reflect the change
    if (task.isCompleted) {
      const updatedTasks = localCompletedTasks.map(t => 
        t.id === taskId ? { ...t, cardOrder: newOrder } : t
      );
      setLocalCompletedTasks(updatedTasks);
    } else {
      const updatedTasks = localActiveTasks.map(t => 
        t.id === taskId ? { ...t, cardOrder: newOrder } : t
      );
      setLocalActiveTasks(updatedTasks);
    }
  };
  
  // Task operations
  const completeTask = (taskId: string) => {
    
    // When completing a task, we need to:
    // 1. Move it from active to completed in local state
    // 2. Assign a new card order at the top of completed tasks
    const task = localActiveTasks.find(t => t.id === taskId);
    if (!task) return;
    
    // Calculate new card order for top of completed list
    let newOrder = 100000;
    if (localCompletedTasks.length > 0) {
      // Place above the highest ordered completed task
      const highestOrderTask = [...localCompletedTasks].sort((a, b) => 
        (b.cardOrder || 0) - (a.cardOrder || 0)
      )[0];
      newOrder = (highestOrderTask?.cardOrder || 0) + 100000;
    }
    
    // Update task with completion status and new order - use silent=true to skip toast
    dbUpdateTask({ 
      taskId, 
      updates: { 
        isCompleted: true,
        cardOrder: newOrder 
      },
      silent: true
    });
    
    // Update local state to reflect the change immediately
    const updatedActiveTasks = localActiveTasks.filter(t => t.id !== taskId);
    const completedTask = { ...task, isCompleted: true, cardOrder: newOrder };
    const updatedCompletedTasks = [
      completedTask,
      ...localCompletedTasks
    ];
    
    setLocalActiveTasks(updatedActiveTasks);
    setLocalCompletedTasks(updatedCompletedTasks);

    // ❌ DISABLED: Sync context for completed task (causing 500 errors)
    // if (task.goalId) {
    //   try {
    //     const allTasks = [...updatedActiveTasks, ...updatedCompletedTasks];
          // Context sync removed - not critical for core functionality
    //   } catch (error) {
    //     // Silent failure - don't break task completion
    //     console.warn('[TaskMutations] Context sync failed for completed task:', error);
    //   }
    // }
  };
  
  const uncompleteTask = (taskId: string) => {
    
    // When uncompleting a task, we need to:
    // 1. Move it from completed to active in local state
    // 2. Assign a new card order at the top of active tasks
    const task = localCompletedTasks.find(t => t.id === taskId);
    if (!task) return;
    
    // Calculate new card order for top of active list
    let newOrder = 100000;
    if (localActiveTasks.length > 0) {
      // Place above the highest ordered active task
      const highestOrderTask = [...localActiveTasks].sort((a, b) => 
        (b.cardOrder || 0) - (a.cardOrder || 0)
      )[0];
      newOrder = (highestOrderTask?.cardOrder || 0) + 100000;
    }
    
    // Update task with completion status and new order - use silent=true to skip toast
    dbUpdateTask({ 
      taskId, 
      updates: { 
        isCompleted: false,
        cardOrder: newOrder 
      },
      silent: true
    });
    
    // Update local state to reflect the change immediately
    const updatedCompletedTasks = localCompletedTasks.filter(t => t.id !== taskId);
    const activatedTask = { ...task, isCompleted: false, cardOrder: newOrder };
    const updatedActiveTasks = [
      activatedTask,
      ...localActiveTasks
    ];
    
    setLocalCompletedTasks(updatedCompletedTasks);
    setLocalActiveTasks(updatedActiveTasks);

    // ❌ DISABLED: Sync context for reactivated task (causing 500 errors)
    // if (task.goalId) {
    //   try {
    //     const allTasks = [...updatedActiveTasks, ...updatedCompletedTasks];
          // Context sync removed - not critical for core functionality
    //   } catch (error) {
    //     // Silent failure - don't break task reactivation
    //     console.warn('[TaskMutations] Context sync failed for reactivated task:', error);
    //   }
    // }
  };
  
  const removeTask = (taskId: string) => {
    
    // Remove from local state first for immediate UI update
    const updatedActiveTasks = localActiveTasks.filter(t => t.id !== taskId);
    const updatedCompletedTasks = localCompletedTasks.filter(t => t.id !== taskId);
    
    setLocalActiveTasks(updatedActiveTasks);
    setLocalCompletedTasks(updatedCompletedTasks);
    
    // Then remove from database
    dbRemoveTask(taskId);
  };

  const updateTask = (taskId: string, updates: Partial<Task>, silent?: boolean) => {
    
    // Special handling for tasks with date changes
    if (updates.dueDate !== undefined) {
      const task = [...localActiveTasks, ...localCompletedTasks].find(t => t.id === taskId);
      const oldDate = task?.dueDate;
      const newDate = updates.dueDate;
      
      if (oldDate !== newDate) {
        // Date changed - reset card order to place at bottom of the new day
        // We use a negative large number to ensure it's at the bottom
        updates.cardOrder = -1000000;
      }
    }
    
    // Handle completion status changes
    if (updates.isCompleted !== undefined) {
      const task = [...localActiveTasks, ...localCompletedTasks].find(t => t.id === taskId);
      if (task && task.isCompleted !== updates.isCompleted) {
        // Status changed - reset card order
        let newOrder = 100000;
        
        if (updates.isCompleted) {
          // Moving to completed - place at the top of completed
          if (localCompletedTasks.length > 0) {
            const highestOrderTask = [...localCompletedTasks].sort((a, b) => 
              (b.cardOrder || 0) - (a.cardOrder || 0)
            )[0];
            newOrder = (highestOrderTask?.cardOrder || 0) + 100000;
          }
        } else {
          // Moving to active - place at the top of active
          if (localActiveTasks.length > 0) {
            const highestOrderTask = [...localActiveTasks].sort((a, b) => 
              (b.cardOrder || 0) - (a.cardOrder || 0)
            )[0];
            newOrder = (highestOrderTask?.cardOrder || 0) + 100000;
          }
        }
        
        updates.cardOrder = newOrder;
      }
    }
    
    // Update local state optimistically
    const updateLocalTask = (task: Task): Task => {
      return { ...task, ...updates };
    };
    
    const updatedActiveTasks = localActiveTasks.map(task => 
      task.id === taskId ? updateLocalTask(task) : task
    );
    
    const updatedCompletedTasks = localCompletedTasks.map(task => 
      task.id === taskId ? updateLocalTask(task) : task
    );
    
    setLocalActiveTasks(updatedActiveTasks);
    setLocalCompletedTasks(updatedCompletedTasks);
    
    // Determine if this update should show a toast
    // If it's just a completion status change, make it silent
    const isSilentUpdate = silent || (
      updates.isCompleted !== undefined && 
      Object.keys(updates).length === 1
    ); // Only isCompleted is being updated or silent flag is explicitly set
  
    // Update in database
    dbUpdateTask({ 
      taskId, 
      updates,
      silent: isSilentUpdate
    });
  };

  const clearCompletedTasks = () => {
    
    // Clear from local state first
    setLocalCompletedTasks([]);
    
    // Then remove each from database
    localCompletedTasks.forEach(task => {
      dbRemoveTask(task.id);
    });
  };

  return {
    addTask,
    updateTaskOrder,
    completeTask,
    uncompleteTask,
    removeTask,
    updateTask,
    clearCompletedTasks
  };
};
